import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { testsAPI } from '../services/api';

function CreateTest() {
    const navigate = useNavigate();
    
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [testPassword, setTestPassword] = useState('');
    const [passwordWarning, setPasswordWarning] = useState('');
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [timeLimit, setTimeLimit] = useState('');
    const [autoGrade, setAutoGrade] = useState(false);
    const [allowedStudents, setAllowedStudents] = useState('');
    const [displayMode, setDisplayMode] = useState('single_page'); // 'single_page' oder 'multi_page'
    const [questions, setQuestions] = useState([]);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const addQuestion = (type) => {
        const newQuestion = {
            id: Date.now(),
            question_type: type,
            question_text: '',
            points: 1,
            options: type === 'multiple_choice' ? ['', '', '', ''] : null,
            correct_answer: type === 'true_false' ? false : '',
            attachment_type: 'none',
            attachment_filename: null,
            attachment_data: null,
            attachment_mimetype: null,
            attachment_url: ''
        };
        setQuestions([...questions, newQuestion]);
    };

    const duplicateQuestion = (questionId) => {
        const questionToDuplicate = questions.find(q => q.id === questionId);
        if (questionToDuplicate) {
            const duplicated = {
                ...questionToDuplicate,
                id: Date.now(),
                question_text: questionToDuplicate.question_text + ' (Kopie)'
            };
            setQuestions([...questions, duplicated]);
        }
    };

    const moveQuestion = (index, direction) => {
        const newQuestions = [...questions];
        if (direction === 'up' && index > 0) {
            [newQuestions[index], newQuestions[index - 1]] = [newQuestions[index - 1], newQuestions[index]];
        } else if (direction === 'down' && index < questions.length - 1) {
            [newQuestions[index], newQuestions[index + 1]] = [newQuestions[index + 1], newQuestions[index]];
        }
        setQuestions(newQuestions);
    };

    const updateQuestion = (id, field, value) => {
        setQuestions(questions.map(q => 
            q.id === id ? { ...q, [field]: value } : q
        ));
    };

    const updateOption = (questionId, index, value) => {
        setQuestions(questions.map(q => {
            if (q.id === questionId) {
                const newOptions = [...q.options];
                newOptions[index] = value;
                return { ...q, options: newOptions };
            }
            return q;
        }));
    };

    const addOption = (questionId) => {
        setQuestions(questions.map(q => {
            if (q.id === questionId && q.options) {
                return { ...q, options: [...q.options, ''] };
            }
            return q;
        }));
    };

    const removeOption = (questionId, index) => {
        setQuestions(questions.map(q => {
            if (q.id === questionId && q.options && q.options.length > 2) {
                const newOptions = q.options.filter((_, i) => i !== index);
                return { ...q, options: newOptions };
            }
            return q;
        }));
    };

    const removeQuestion = (id) => {
        setQuestions(questions.filter(q => q.id !== id));
    };

    const handlePasswordBlur = async () => {
        if (!testPassword) return;

        try {
            const response = await testsAPI.checkPassword(testPassword);
            if (response.data.exists) {
                setPasswordWarning(`⚠️ Achtung: Das Passwort "${testPassword}" wird bereits für den Test "${response.data.test.title}" verwendet!`);
            } else {
                setPasswordWarning('');
            }
        } catch (err) {
            console.error('Fehler beim Passwort-Check:', err);
        }
    };

    const handleFileUpload = (questionId, event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (file.size > 10 * 1024 * 1024) {
            alert('Datei ist zu groß! Maximum 10MB.');
            return;
        }

        const allowedTypes = [
            'image/jpeg',
            'image/jpg',
            'image/png',
            'image/gif',
            'image/webp',
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        ];

        if (!allowedTypes.includes(file.type)) {
            alert('Dateityp nicht erlaubt! Nur Bilder, PDF und Word-Dokumente.');
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            const base64Data = e.target.result.split(',')[1];
            
            setQuestions(questions.map(q => 
                q.id === questionId ? {
                    ...q,
                    attachment_filename: file.name,
                    attachment_data: base64Data,
                    attachment_mimetype: file.type
                } : q
            ));
        };
        reader.readAsDataURL(file);
    };

    const removeAttachment = (questionId) => {
        setQuestions(questions.map(q => 
            q.id === questionId ? {
                ...q,
                attachment_type: 'none',
                attachment_filename: null,
                attachment_data: null,
                attachment_mimetype: null,
                attachment_url: ''
            } : q
        ));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const studentsArray = allowedStudents
                .split('\n')
                .map(s => s.trim())
                .filter(s => s.length > 0);

            const preparedQuestions = questions.map(({ id, attachment_type, ...q }) => {
                if (attachment_type === 'url') {
                    return {
                        ...q,
                        attachment_filename: null,
                        attachment_data: null,
                        attachment_mimetype: null
                    };
                }
                if (attachment_type === 'file') {
                    return {
                        ...q,
                        attachment_url: null
                    };
                }
                return {
                    ...q,
                    attachment_filename: null,
                    attachment_data: null,
                    attachment_mimetype: null,
                    attachment_url: null
                };
            });

            const testData = {
                title,
                description,
                test_password: testPassword,
                time_limit: (showAdvanced && timeLimit) ? parseInt(timeLimit) : null,
                auto_grade: showAdvanced ? autoGrade : false,
                display_mode: displayMode,
                questions: preparedQuestions,
                allowed_students: studentsArray
            };

            await testsAPI.createTest(testData);
            navigate('/dashboard');
        } catch (err) {
            setError(err.response?.data?.details || err.response?.data?.error || 'Fehler beim Erstellen des Tests');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1>Neuen Test erstellen</h1>
                <button onClick={() => navigate('/dashboard')} style={styles.backButton}>
                    ← Zurück
                </button>
            </div>

            {error && <div style={styles.error}>{error}</div>}

            <form onSubmit={handleSubmit}>
                <div style={styles.section}>
                    <h2>Test-Informationen</h2>
                    
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Titel *</label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            required
                            style={styles.input}
                            placeholder="z.B. Mathe Test Klasse 5"
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Beschreibung</label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            style={styles.textarea}
                            placeholder="Kurze Beschreibung des Tests"
                            rows="3"
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Test-Passwort *</label>
                        <input
                            type="text"
                            value={testPassword}
                            onChange={(e) => {
                                setTestPassword(e.target.value);
                                setPasswordWarning('');
                            }}
                            onBlur={handlePasswordBlur}
                            required
                            style={styles.input}
                            placeholder="Passwort für Schüler"
                        />
                        {passwordWarning && (
                            <div style={styles.passwordWarning}>
                                {passwordWarning}
                            </div>
                        )}
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Erlaubte Schüler (einer pro Zeile) *</label>
                        <textarea
                            value={allowedStudents}
                            onChange={(e) => setAllowedStudents(e.target.value)}
                            required
                            style={styles.textarea}
                            placeholder="Anna Schmidt&#10;Tom Müller&#10;Lisa Weber"
                            rows="5"
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Anzeige-Modus *</label>
                        <div style={styles.radioGroup}>
                            <label style={styles.radioLabel}>
                                <input
                                    type="radio"
                                    name="displayMode"
                                    value="single_page"
                                    checked={displayMode === 'single_page'}
                                    onChange={(e) => setDisplayMode(e.target.value)}
                                    style={styles.radio}
                                />
                                <div>
                                    <strong>Alle Fragen auf einer Seite</strong>
                                    <small style={styles.hint}>Schüler sehen alle Fragen gleichzeitig und können scrollen</small>
                                </div>
                            </label>
                            <label style={styles.radioLabel}>
                                <input
                                    type="radio"
                                    name="displayMode"
                                    value="multi_page"
                                    checked={displayMode === 'multi_page'}
                                    onChange={(e) => setDisplayMode(e.target.value)}
                                    style={styles.radio}
                                />
                                <div>
                                    <strong>Eine Frage pro Seite</strong>
                                    <small style={styles.hint}>Schüler navigieren mit "Weiter"/"Zurück"-Buttons durch die Fragen</small>
                                </div>
                            </label>
                        </div>
                    </div>

                    <div style={styles.advancedSection}>
                        <button 
                            type="button"
                            onClick={() => setShowAdvanced(!showAdvanced)}
                            style={styles.advancedToggle}
                        >
                            {showAdvanced ? '▼' : '▶'} Erweiterte Optionen
                        </button>

                        {showAdvanced && (
                            <div style={styles.advancedContent}>
                                <div style={styles.formGroup}>
                                    <label style={styles.checkboxLabel}>
                                        <input
                                            type="checkbox"
                                            checked={autoGrade}
                                            onChange={(e) => setAutoGrade(e.target.checked)}
                                            style={styles.checkbox}
                                        />
                                        Automatisch bewerten
                                    </label>
                                    <small style={styles.hint}>
                                        {autoGrade ? 'Multiple Choice, Wahr/Falsch und Zuordnung werden automatisch bewertet. Du kannst Punkte pro Frage vergeben.' : 'Alle Antworten werden manuell bewertet'}
                                    </small>
                                </div>

                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Zeitlimit (Minuten)</label>
                                    <input
                                        type="number"
                                        value={timeLimit}
                                        onChange={(e) => setTimeLimit(e.target.value)}
                                        style={styles.inputSmall}
                                        placeholder="Optional"
                                        min="1"
                                    />
                                    <small style={styles.hint}>
                                        Leer lassen für kein Zeitlimit
                                    </small>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div style={styles.section}>
                    <div style={styles.sectionHeader}>
                        <h2>Fragen ({questions.length})</h2>
                        <div style={styles.questionButtons}>
                            <button type="button" onClick={() => addQuestion('multiple_choice')} style={styles.addButton}>
                                + Multiple Choice
                            </button>
                            <button type="button" onClick={() => addQuestion('true_false')} style={styles.addButton}>
                                + Wahr/Falsch
                            </button>
                            <button type="button" onClick={() => addQuestion('free_text')} style={styles.addButton}>
                                + Freitext
                            </button>
                        </div>
                    </div>

                    {questions.map((question, index) => (
                        <div key={question.id} style={styles.questionCard}>
                            <div style={styles.questionHeader}>
                                <h3>Frage {index + 1} - {getQuestionTypeName(question.question_type)}</h3>
                                <div style={styles.questionActions}>
                                    <button 
                                        type="button" 
                                        onClick={() => moveQuestion(index, 'up')}
                                        disabled={index === 0}
                                        style={{...styles.iconButton, ...(index === 0 ? styles.iconButtonDisabled : {})}}
                                        title="Nach oben"
                                    >
                                        ▲
                                    </button>
                                    <button 
                                        type="button" 
                                        onClick={() => moveQuestion(index, 'down')}
                                        disabled={index === questions.length - 1}
                                        style={{...styles.iconButton, ...(index === questions.length - 1 ? styles.iconButtonDisabled : {})}}
                                        title="Nach unten"
                                    >
                                        ▼
                                    </button>
                                    <button 
                                        type="button" 
                                        onClick={() => duplicateQuestion(question.id)}
                                        style={styles.iconButton}
                                        title="Duplizieren"
                                    >
                                        📋
                                    </button>
                                    <button 
                                        type="button" 
                                        onClick={() => removeQuestion(question.id)}
                                        style={styles.removeButton}
                                    >
                                        Entfernen
                                    </button>
                                </div>
                            </div>

                            <div style={styles.formGroup}>
                                <label style={styles.label}>Fragetext *</label>
                                <textarea
                                    value={question.question_text}
                                    onChange={(e) => updateQuestion(question.id, 'question_text', e.target.value)}
                                    required
                                    style={styles.textarea}
                                    rows="3"
                                    placeholder="Gib hier deine Frage ein..."
                                />
                            </div>

                            {showAdvanced && autoGrade && (
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Punkte</label>
                                    <input
                                        type="number"
                                        value={question.points}
                                        onChange={(e) => updateQuestion(question.id, 'points', parseInt(e.target.value) || 1)}
                                        style={styles.inputSmall}
                                        min="1"
                                    />
                                </div>
                            )}

                            <div style={styles.attachmentSection}>
                                <label style={styles.label}>Anhang hinzufügen (optional)</label>
                                <div style={styles.attachmentTypeSelector}>
                                    <label style={styles.radioLabel}>
                                        <input
                                            type="radio"
                                            name={`attachment_type_${question.id}`}
                                            value="none"
                                            checked={question.attachment_type === 'none'}
                                            onChange={() => {
                                                updateQuestion(question.id, 'attachment_type', 'none');
                                                removeAttachment(question.id);
                                            }}
                                            style={styles.radio}
                                        />
                                        <span>Kein Anhang</span>
                                    </label>
                                    <label style={styles.radioLabel}>
                                        <input
                                            type="radio"
                                            name={`attachment_type_${question.id}`}
                                            value="file"
                                            checked={question.attachment_type === 'file'}
                                            onChange={() => updateQuestion(question.id, 'attachment_type', 'file')}
                                            style={styles.radio}
                                        />
                                        <span>📎 Datei hochladen</span>
                                    </label>
                                    <label style={styles.radioLabel}>
                                        <input
                                            type="radio"
                                            name={`attachment_type_${question.id}`}
                                            value="url"
                                            checked={question.attachment_type === 'url'}
                                            onChange={() => updateQuestion(question.id, 'attachment_type', 'url')}
                                            style={styles.radio}
                                        />
                                        <span>🔗 URL-Link</span>
                                    </label>
                                </div>

                                {question.attachment_type === 'file' && (
                                    <div style={styles.attachmentContent}>
                                        {question.attachment_filename ? (
                                            <div style={styles.attachmentPreview}>
                                                <div style={styles.attachmentInfo}>
                                                    <strong>📎 {question.attachment_filename}</strong>
                                                    {question.attachment_mimetype?.startsWith('image/') && (
                                                        <div style={styles.imagePreview}>
                                                            <img 
                                                                src={`data:${question.attachment_mimetype};base64,${question.attachment_data}`}
                                                                alt="Vorschau"
                                                                style={styles.previewImage}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                                <button 
                                                    type="button"
                                                    onClick={() => removeAttachment(question.id)}
                                                    style={styles.removeAttachmentButton}
                                                >
                                                    Entfernen
                                                </button>
                                            </div>
                                        ) : (
                                            <>
                                                <input
                                                    type="file"
                                                    accept="image/*,.pdf,.doc,.docx"
                                                    onChange={(e) => handleFileUpload(question.id, e)}
                                                    style={styles.fileInput}
                                                />
                                                <small style={styles.hint}>
                                                    Erlaubt: Bilder (JPG, PNG, GIF, WebP), PDF, Word-Dokumente. Maximale Größe: 10MB
                                                </small>
                                            </>
                                        )}
                                    </div>
                                )}

                                {question.attachment_type === 'url' && (
                                    <div style={styles.attachmentContent}>
                                        <input
                                            type="url"
                                            value={question.attachment_url}
                                            onChange={(e) => updateQuestion(question.id, 'attachment_url', e.target.value)}
                                            style={styles.input}
                                            placeholder="https://example.com/bild.jpg oder https://youtube.com/watch?v=..."
                                        />
                                        <small style={styles.hint}>
                                            Füge einen Link zu einem Bild, Video (YouTube), PDF oder einer Website ein.
                                        </small>
                                    </div>
                                )}
                            </div>

                            {question.question_type === 'multiple_choice' && (
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Antwortmöglichkeiten *</label>
                                    {question.options.map((option, i) => (
                                        <div key={i} style={styles.optionRow}>
                                            <input
                                                type="text"
                                                value={option}
                                                onChange={(e) => updateOption(question.id, i, e.target.value)}
                                                style={styles.input}
                                                placeholder={`Option ${i + 1}`}
                                                required
                                            />
                                            {question.options.length > 2 && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeOption(question.id, i)}
                                                    style={styles.removeOptionButton}
                                                    title="Option entfernen"
                                                >
                                                    ✕
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                    <button
                                        type="button"
                                        onClick={() => addOption(question.id)}
                                        style={styles.addOptionButton}
                                    >
                                        + Weitere Option hinzufügen
                                    </button>
                                    {showAdvanced && autoGrade && (
                                        <>
                                            <label style={styles.label}>Richtige Antwort *</label>
                                            <select
                                                value={question.correct_answer}
                                                onChange={(e) => updateQuestion(question.id, 'correct_answer', e.target.value)}
                                                style={styles.select}
                                                required
                                            >
                                                <option value="">Wähle die richtige Antwort</option>
                                                {question.options.map((option, i) => (
                                                    <option key={i} value={option}>{option || `Option ${i + 1}`}</option>
                                                ))}
                                            </select>
                                        </>
                                    )}
                                </div>
                            )}

                            {question.question_type === 'true_false' && showAdvanced && autoGrade && (
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Richtige Antwort *</label>
                                    <select
                                        value={question.correct_answer}
                                        onChange={(e) => updateQuestion(question.id, 'correct_answer', e.target.value === 'true')}
                                        style={styles.select}
                                        required
                                    >
                                        <option value="">Wähle...</option>
                                        <option value="true">Wahr</option>
                                        <option value="false">Falsch</option>
                                    </select>
                                </div>
                            )}

                            {question.question_type === 'free_text' && (
                                <div style={styles.info}>
                                    💡 Freitext-Fragen müssen manuell bewertet werden
                                </div>
                            )}
                        </div>
                    ))}

                    {questions.length === 0 && (
                        <div style={styles.emptyQuestions}>
                            Noch keine Fragen hinzugefügt. Klicke auf einen der Buttons oben.
                        </div>
                    )}
                </div>

                <div style={styles.submitSection}>
                    <button type="submit" disabled={loading || questions.length === 0} style={styles.submitButton}>
                        {loading ? 'Wird erstellt...' : 'Test erstellen'}
                    </button>
                </div>
            </form>
        </div>
    );
}

function getQuestionTypeName(type) {
    const names = {
        'multiple_choice': 'Multiple Choice',
        'true_false': 'Wahr/Falsch',
        'free_text': 'Freitext',
        'matching': 'Zuordnung'
    };
    return names[type] || type;
}

const styles = {
    container: {
        padding: '2rem',
        maxWidth: '900px',
        margin: '0 auto',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem',
    },
    backButton: {
        padding: '0.5rem 1rem',
        backgroundColor: '#6c757d',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
    },
    section: {
        backgroundColor: 'white',
        padding: '2rem',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        marginBottom: '2rem',
    },
    sectionHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
        gap: '1rem',
    },
    formGroup: {
        marginBottom: '1.5rem',
    },
    label: {
        display: 'block',
        marginBottom: '0.5rem',
        fontWeight: 'bold',
        color: '#333',
    },
    checkboxLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        fontWeight: 'bold',
        color: '#333',
    },
    checkbox: {
        width: '20px',
        height: '20px',
        cursor: 'pointer',
    },
    input: {
        width: '100%',
        padding: '0.75rem',
        border: '1px solid #ddd',
        borderRadius: '4px',
        fontSize: '1rem',
        boxSizing: 'border-box',
        marginBottom: '0.5rem',
    },
    inputSmall: {
        width: '150px',
        padding: '0.75rem',
        border: '1px solid #ddd',
        borderRadius: '4px',
        fontSize: '1rem',
    },
    textarea: {
        width: '100%',
        padding: '0.75rem',
        border: '1px solid #ddd',
        borderRadius: '4px',
        fontSize: '1rem',
        boxSizing: 'border-box',
        fontFamily: 'inherit',
        resize: 'vertical',
    },
    select: {
        width: '100%',
        padding: '0.75rem',
        border: '1px solid #ddd',
        borderRadius: '4px',
        fontSize: '1rem',
        boxSizing: 'border-box',
    },
    radioGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
    },
    hint: {
        display: 'block',
        marginTop: '0.25rem',
        color: '#666',
        fontSize: '0.85rem',
        fontStyle: 'italic',
    },
    passwordWarning: {
        marginTop: '0.5rem',
        padding: '0.75rem',
        backgroundColor: '#fff3cd',
        border: '1px solid #ffc107',
        borderRadius: '4px',
        color: '#856404',
        fontWeight: 'bold',
    },
    advancedSection: {
        marginTop: '2rem',
        paddingTop: '1.5rem',
        borderTop: '2px solid #eee',
    },
    advancedToggle: {
        padding: '0.75rem 1.5rem',
        backgroundColor: '#6c757d',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '1rem',
        fontWeight: 'bold',
        width: '100%',
        textAlign: 'left',
    },
    advancedContent: {
        marginTop: '1rem',
        padding: '1.5rem',
        backgroundColor: '#f8f9fa',
        borderRadius: '4px',
    },
    questionButtons: {
        display: 'flex',
        gap: '0.5rem',
        flexWrap: 'wrap',
    },
    addButton: {
        padding: '0.5rem 1rem',
        backgroundColor: '#28a745',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '0.9rem',
    },
    questionCard: {
        backgroundColor: '#f8f9fa',
        padding: '1.5rem',
        borderRadius: '8px',
        marginBottom: '1rem',
        border: '1px solid #dee2e6',
    },
    questionHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1rem',
        flexWrap: 'wrap',
        gap: '1rem',
    },
    questionActions: {
        display: 'flex',
        gap: '0.5rem',
        alignItems: 'center',
    },
    iconButton: {
        padding: '0.5rem 0.75rem',
        backgroundColor: '#007bff',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '0.9rem',
    },
    iconButtonDisabled: {
        backgroundColor: '#ccc',
        cursor: 'not-allowed',
    },
    removeButton: {
        padding: '0.5rem 1rem',
        backgroundColor: '#dc3545',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '0.9rem',
    },
    optionRow: {
        display: 'flex',
        gap: '0.5rem',
        alignItems: 'center',
        marginBottom: '0.5rem',
    },
    removeOptionButton: {
        padding: '0.5rem 0.75rem',
        backgroundColor: '#dc3545',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '1rem',
        flexShrink: 0,
    },
    addOptionButton: {
        padding: '0.5rem 1rem',
        backgroundColor: '#17a2b8',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '0.9rem',
        marginTop: '0.5rem',
        marginBottom: '1rem',
    },
    attachmentSection: {
        marginBottom: '1.5rem',
        padding: '1rem',
        backgroundColor: '#fff',
        borderRadius: '4px',
        border: '2px dashed #dee2e6',
    },
    attachmentTypeSelector: {
        display: 'flex',
        gap: '1.5rem',
        marginTop: '0.75rem',
        marginBottom: '1rem',
        padding: '1rem',
        backgroundColor: '#e9ecef',
        borderRadius: '4px',
        flexWrap: 'wrap',
    },
    radioLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        cursor: 'pointer',
        padding: '0.5rem',
        borderRadius: '4px',
    },
    radio: {
        width: '18px',
        height: '18px',
        cursor: 'pointer',
    },
    attachmentContent: {
        marginTop: '1rem',
    },
    fileInput: {
        width: '100%',
        padding: '0.75rem',
        border: '2px solid #007bff',
        borderRadius: '4px',
        fontSize: '1rem',
        boxSizing: 'border-box',
        backgroundColor: '#fff',
    },
    attachmentPreview: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        padding: '1rem',
        backgroundColor: '#e7f3ff',
        borderRadius: '4px',
        border: '2px solid #0066cc',
    },
    attachmentInfo: {
        flex: 1,
    },
    imagePreview: {
        marginTop: '1rem',
    },
    previewImage: {
        maxWidth: '300px',
        maxHeight: '300px',
        borderRadius: '4px',
        border: '1px solid #ddd',
    },
    removeAttachmentButton: {
        padding: '0.5rem 1rem',
        backgroundColor: '#dc3545',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '0.9rem',
        whiteSpace: 'nowrap',
    },
    info: {
        backgroundColor: '#d1ecf1',
        color: '#0c5460',
        padding: '0.75rem',
        borderRadius: '4px',
        fontSize: '0.9rem',
    },
    emptyQuestions: {
        textAlign: 'center',
        padding: '3rem',
        color: '#666',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
    },
    error: {
        backgroundColor: '#fee',
        color: '#c33',
        padding: '1rem',
        borderRadius: '4px',
        marginBottom: '1rem',
    },
    submitSection: {
        textAlign: 'center',
    },
    submitButton: {
        padding: '1rem 3rem',
        backgroundColor: '#007bff',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        fontSize: '1.1rem',
        fontWeight: 'bold',
        cursor: 'pointer',
    },
};

export default CreateTest;
